/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import BaseControl from 'scripts/base-control';
import appContext from 'scripts/app-context';
import device from 'scripts/utils/device';
import stacheHelpers from 'helpers/client';
import can from 'can';
import urlUtil from 'scripts/utils/url';
import { customPathCompare } from 'blocks/navigation/header/header';
import dtm from 'scripts/services/dtm';

const searchPagePath = window.globalSettings.pagePaths.search;
const simulation = window.globalSettings.simulation;
const KEY_CODE_ESC = 27;
const KEY_CODE_TAB = 9;

export const HamburgerMenu = BaseControl.extend({
    init: function() {
        this._super();
        appContext.model.bind(
            'hamburger-menu-toggle',
            this.hamburgerMenuToggle.bind(this),
        );
        appContext.model.bind(
            'focus-menu-button',
            this.focusMenuButton.bind(this),
        );
        this.setActiveLink();
        if (simulation) {
            this.element
                .find('.s-hamburger-menu-nav-wrapper.s-simulation')
                .show();
        }
        if (device.isIos || device.isTouch) {
            this.checkScroll();
        }
        can.$(document).on(
            'keydown',
            function(e) {
                if (
                    e &&
                    e.keyCode === KEY_CODE_ESC &&
                    this.element.hasClass('is-expanded')
                ) {
                    appContext.model.dispatch('hamburger-menu-toggle', [false]);
                    appContext.model.dispatch('focus-menu-button');
                }
            }.bind(this),
        );
        this.trapFocus();
    },

    trapFocus() {
        const focusableEls = this.element.find(
            'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])',
        );
        const firstFocusableEl = focusableEls[0];
        const lastFocusableEl = focusableEls[focusableEls.length - 1];

        this.element.on('keydown', function(e) {
            const isTabPressed = e.key === 'Tab' || e.keyCode === KEY_CODE_TAB;
            if (!isTabPressed) {
                return;
            }

            if (e.shiftKey) {
                if (document.activeElement === firstFocusableEl) {
                    lastFocusableEl.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === lastFocusableEl) {
                    firstFocusableEl.focus();
                    e.preventDefault();
                }
            }
        });
    },

    setActiveLink() {
        this.$element.find('.s-hamburger-menu-nav__item').each((i, el) => {
            var $el = this.$(el);

            if (
                stacheHelpers.isActiveLink &&
                stacheHelpers.isActiveLink(
                    $el.attr('href'),
                    this.options.pagePath,
                    { customPathCompare },
                )
            ) {
                $el.addClass('is-active');
            }

            return $el;
        });
    },

    checkScroll() {
        const navContainer = this.element.find('.s-hamburger-menu-nav');
        navContainer.scrollTop(1).on('scroll', (ev) => {
            let scrollBottom =
                ev.target.scrollHeight -
                (ev.target.offsetHeight + ev.target.scrollTop);
            if (scrollBottom <= 0) {
                scrollBottom = 0;
            }
            if (scrollBottom === 0) {
                ev.target.scrollTop = ev.target.scrollTop - 1;
            }
            if (ev.target.scrollTop === 0) {
                ev.target.scrollTop = 1;
            }
        });
    },

    focusMenuButton: function() {
        $('.s-navigation-menu .s-hamburger')
            .first()
            .focus();
    },

    hamburgerMenuToggle: function(ev, flag) {
        this.$element.toggleClass('is-expanded', flag);
        appContext.model.dispatch('page-scroll-toggle', [flag]);
        $('.s-navigation-menu .s-hamburger')
            .first()
            .attr('aria-expanded', flag);

        if (flag) {
            this.$element.find('.js-start-focus').focus();
        }
    },

    '.js-close-on-click click': function($el, event) {
        appContext.model.dispatch('hamburger-menu-toggle', [false]);
        const isKeyPressed = event.originalEvent.detail === 0;
        if (isKeyPressed) {
            appContext.model.dispatch('focus-menu-button');
        }
    },

    '.s-hamburger-menu-nav__simulation click'(el, ev) {
        ev.preventDefault();
        if (!this.simulationContainer) {
            this.simulationContainer = can.$(
                document.querySelector('.c-simulation-config-wrap'),
            );
        }
        this.simulationContainer.toggleClass('is-visible', true);
        $('.main-content').css(
            'padding-top',
            $('.c-simulation-config ').height(),
        );
        appContext.model.dispatch('hamburger-menu-toggle', [false]);
    },

    '.s-hamburger-menu-nav__item click'(el, ev) {
        if (ev.ctrlKey) {
            return;
        }

        const href = el.attr('href');

        ev.preventDefault();
        dtm.trackMenuItemClick(el.text(), href);
        window.open(href, el.attr('target'));
    },

    '.s-hamburger-menu-search submit': function(el, ev) {
        ev.preventDefault();
        let query = encodeURIComponent(
            el.find('input.s-fieldset__input-text').val(),
        );
        if (!query) {
            return;
        }

        const {
            hostname: absHostname,
            pathname: absPathname,
            href,
        } = urlUtil.parseUrl(searchPagePath);

        let absoluteUrl = `${href}#!&q=${query}`;

        if (
            absHostname !== urlUtil.getHostName() ||
            absPathname !== urlUtil.getPathName()
        ) {
            location.assign(absoluteUrl);
        } else {
            urlUtil.pushState(absoluteUrl);
            appContext.model.dispatch('hamburger-menu-toggle', [false]);
            appContext.model.dispatch('hamburger-search', [query]);
        }
    },
});
